import { graphql, Script } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';

import { useTranslation } from '@klaviyo-mkt/gatsby-components/hooks/useTranslation';
import { ModulesGrid } from '@klaviyo-mkt/gatsby-components/utils/grid';
import KlaviyoForm from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-form';
import HubspotForm from '@klaviyo-mkt/gatsby-components/atoms/hubspot-form';
import { useBreadcrumbs, useShouldDisplayBreadcrumbs } from '@klaviyo-mkt/gatsby-theme-marketing/src/hooks/useBreadcrumbs';
import EditorialLayout from '@klaviyo-mkt/gatsby-components/organisms/editorial-layout';

import { useBlogMentions } from '../../hooks/useBlogMentions';
import GlobalHead from '../layout/head';
import Layout from '../layout';
import LayoutTheme from './theme';
import * as styles from './styles/editorial.module.scss';

import type { IEditorialData, IPostContext, IPostTemplateData } from './types';
import type { HeadProps, PageProps } from 'gatsby';

const PostTemplate = ({ data, pageContext, ...pageProps }: PageProps<IPostTemplateData, IPostContext>) => {
  const {
    typeNameDataKey,
    uri,
    subnavMenu,
  } = pageContext;
  const layoutData = data[typeNameDataKey] as IEditorialData;

  // get the current language
  const { i18n } = useTranslation();

  /* get window location data for editorial layout */
  const locationObject = useLocation();
  const windowLocation = `${locationObject.origin}${locationObject.pathname}${locationObject.search}`;
  
  // pass in proper breadcrumb data to the editorial layout
  const { displayBreadcrumbs } = layoutData?.templatesEditorial?.metaHeaderCopy || {};
  const { postTitle: { shortTitle } = {} } = layoutData?.templatesEditorial || {};
  const breadcrumbs = useBreadcrumbs(uri, shortTitle, i18n.language);
  const { shouldShowBreadcrumbs } = useShouldDisplayBreadcrumbs(uri, 'default', displayBreadcrumbs, i18n.language);
  const showBreadCrumbs = shouldShowBreadcrumbs && !subnavMenu?.items?.length;

  const headerProps = {
    customers: layoutData?.customers,
    featuredImage: layoutData?.featuredImage,
    industries: layoutData?.industries,
    platforms: layoutData?.platforms,
  };

  const footerForm = layoutData?.templatesEditorial?.footerForm;

  return (
    <Layout
      {...pageProps}
      {...pageContext}
      bodyClass={['editorial', typeNameDataKey].join(' ')}
      showBreadcrumbs={false}
      templateName="Editorial"
      theme={LayoutTheme}
      scrollIndicator={pageContext?.isPost}
    >
      <EditorialLayout
        authors={layoutData?.authors?.nodes}
        isPost={pageContext?.isPost}
        windowLocation={windowLocation}
        showBreadcrumbs={showBreadCrumbs}
        breadcrumbs={breadcrumbs}
        header={headerProps}
        title={layoutData.title}
        format={pageContext.format || 'rich'}
        content={layoutData?.content || ''}
        customPadding={{ customPaddingTop: 'xs', customPaddingBottom: 'xs' }}
        contained={true}
        inlinePadding={true}
        backgroundColor="white"
        fadeIn={0}
        templatesEditorial={layoutData.templatesEditorial}
      />
      {footerForm?.useForm && footerForm.formType === 'klaviyo' && (
        <KlaviyoForm
          backgroundColor={footerForm.form.klaviyoForm?.backgroundColor || 'light'}
          formId={footerForm.form.klaviyoForm?.formId}
        />
      )}
      {footerForm?.useForm && footerForm.formType === 'hubspot' && (
        <div className={styles.hubspotFormColor}>
          <ModulesGrid className={styles.styledPost}>
            <div className={styles.hubspotFormContainer}>
              <HubspotForm
                singleLineForm={true}
                formId={footerForm.form.hubspotForm?.formId}
                hubspotHeaderText={footerForm.form.hubspotForm?.formHeadline}
              />
            </div>
          </ModulesGrid>
        </div>
      )}
    </Layout>
  );
};

export const query = graphql`
  query EditorialTemplate(
    $id: String!,
    $wpBfcmPage: Boolean = false,
    $wpCaseStudy: Boolean = false,
    $wpGlossaryPage: Boolean = false,
    $wpIndustryPage: Boolean = false,
    $wpIntegratePage: Boolean = false,
    $wpLegalContent: Boolean = false,
    $wpNewsroomArticle: Boolean = false,
    $wpPage: Boolean = false,
    $wpPost: Boolean = false,
    $wpPrivacyPage: Boolean = false,
    $wpProductPage: Boolean = false,
    $wpProductsPage: Boolean = false,
    $wpSolutionsPage: Boolean = false,
  ) {
    wpBfcmPage(id: {eq: $id}) @include(if: $wpBfcmPage) {
      ...BfcmPage
    }
    wpCaseStudy(id: {eq: $id}) @include(if: $wpCaseStudy) {
      ...CaseStudy
    }
    wpGlossaryPage(id: {eq: $id}) @include(if: $wpGlossaryPage) {
      ...GlossaryPage
    }
    wpIndustryPage(id: {eq: $id}) @include(if: $wpIndustryPage) {
      ...IndustryPage
    }
    wpIntegratePage(id: {eq: $id}) @include(if: $wpIntegratePage) {
      ...IntegratePage
    }
    wpLegalContent(id: {eq: $id}) @include(if: $wpLegalContent) {
      ...LegalContent
    }
    wpNewsroomArticle(id: {eq: $id}) @include(if: $wpNewsroomArticle) {
      ...NewsroomArticleDetails
    }
    wpPage(id: {eq: $id}) @include(if: $wpPage) {
      ...EditorialPage
    }
    wpPost(id: {eq: $id}) @include(if: $wpPost) {
      ...EditorialPost
    }
    wpPrivacyPage(id: {eq: $id}) @include(if: $wpPrivacyPage) {
      ...PrivacyPageContent
    }
    wpProductPage(id: {eq: $id}) @include(if: $wpProductPage) {
      ...ProductPage
    }
    wpProductsPage(id: {eq: $id}) @include(if: $wpProductsPage) {
      ...ProductsPage
    }
    wpSolutionsPage(id: {eq: $id}) @include(if: $wpSolutionsPage) {
      ...SolutionsPage
    }
  }
`;

export const Head = ({
  data,
  location,
  pageContext,
}: HeadProps<IPostTemplateData, IPostContext>) => {
  const { shortTitle = '', typeNameDataKey } = pageContext;
  const layoutData = data[typeNameDataKey];
  const includeFaqSchema = (layoutData?.content || '').includes('data-includefaqschema="true"');

  return (
    <GlobalHead
      authors={layoutData?.authors}
      excerpt={layoutData?.excerpt}
      isPost={pageContext.isPost}
      mentions={useBlogMentions(layoutData?.content || '')}
      pathname={location.pathname}
      seo={layoutData?.headlessSeo}
      shortTitle={shortTitle}
      translations={layoutData?.translations}
      typeNameDataKey={typeNameDataKey}
    >
      {data[typeNameDataKey]?.content.includes('https://platform.twitter.com/widgets.js') && <Script id="twitter-wjs" src="https://platform.twitter.com/widgets.js"/>}
      {data[typeNameDataKey]?.content.includes('https://player.vimeo.com/video') && <Script id="vimeo-player" src="//player.vimeo.com/api/player.js"/>}
      {data[typeNameDataKey]?.content.includes('https://www.tiktok.com/embed.js') && <Script id="tiktok-player" src="https://www.tiktok.com/embed.js"/>}
      {includeFaqSchema ? (<html itemScope itemType="https://schema.org/FAQPage" />) : null}
    </GlobalHead>
  );
};

export default PostTemplate;
